import { zodResolver } from '@hookform/resolvers/zod'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { Cuisine50Options } from 'models/cuisine_50'
import { usePreferences } from 'context/preferences/PreferencesContext'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { getSingularUnit } from 'utils/reportingUnits'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { IColumnVisibility } from 'components/DataTable/types'
import { DataTableContainer } from 'components/DataTable/UI'
import { useDataTableContext } from 'components/DataTable/DataTableContext'
import { ProductModal } from 'components/ProductFlow/ProductModal'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import {
  formatDateWithTimeAndMinutes,
  formatPercentage,
  formatTwoDecimal,
  formatInteger,
} from 'utils/formatting'
import { Product, Variant } from 'models/product'
import { handleFormError } from 'utils/handleFormError'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { TableSearch } from 'components/Filters/TableSearch'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import ProductsFilterset from './ProductsFilterset'
import * as S from '../CommonTable.styles'
import { FilterIdentifier } from 'models/saved_view'
import { getTableQueryKey } from 'utils/getTableQueryKey'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useAuth } from 'context/authentication/useAuth'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import { PiDownloadSimple, PiExport } from 'react-icons/pi'
import { IconMiniButton } from 'components/FbUI/FbButton'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { VariantSearchTable } from 'components/Tables/VariantSearchTable/VariantSearchTable'
import {
  useListVariantsCatalog,
  useListProductsCatalog,
  useGetSyndigoSyncStatus,
  exportProductsCatalogAsBlob,
  exportVariantsCatalogAsBlob,
  useCreateLegacyProduct,
  useUpdateLegacyProduct,
  useDeleteLegacyProduct,
  type CreateLegacyProductMutationRequest,
  type UpdateLegacyProductMutationRequest,
} from 'services/products'
import { useProductsViewModeStore } from 'stores/productsViewModeStore'
import { getStatusColor } from 'utils/productStatus'
import { StandaloneVariantModal } from 'components/ProductFlow/StandaloneVariantModal'
import { PillListCell } from 'components/Tables/CommonTableCells/PillListCell'
import { PillOverflowChip } from 'components/Tables/CommonTableCells/PillOverflowChip'
import { downloadFile } from 'utils/csvDownloader'
import { toast } from 'react-toastify'
import { ProductCatalog } from 'services/generated'

export interface ProductFormValues {
  name: string
  format: string
  product_format: string
  price_per_case: number
  mfg_avg_list_price_per_lb: number
  avg_serving_size_per_lb: number
  est_monthly_churn_rate: number
  value_per_consumer_new_trial: number
  lbs_per_case: number
  current_aided_awareness: number
  current_national_trial_perc: number
  value_per_consumer_new_informed: number
  estimated_sales_potential: {
    default_sales?: number
    cuisine_sales?: Record<string, number>
    is_not_recurring?: boolean
  }
  unit: string
  variant_count?: number
  variant_names?: string[]
  variants?: Variant[]
}

export const productShape = z.object({
  name: z.string().min(1, 'Name is required').max(60, 'Name is too long'),
  // format: z.string().optional(), TODO: Do we still need this field?
  // product_format: z.string().min(1, 'Product format is required'), TODO: Do we still need this field?
  avg_serving_size_per_lb: z.number().optional().default(1),
  mfg_avg_list_price_per_lb: z
    .number({
      invalid_type_error: 'Average price is required',
    })
    .gt(0, 'Average price must be greater than 0'),
  est_monthly_churn_rate: z
    .number({ invalid_type_error: 'Monthly churn rate is required' })
    .gt(0, 'Churn rate must be greater than 0')
    .max(100, 'Churn rate cannot exceed 100%')
    .default(1),
  // Unit field is UI-only for display purposes (e.g., showing "lbs" suffix).
  // Not persisted to backend - excluded from API payload.
  unit: z.string().optional(),
  value_per_consumer_new_trial: z
    .number()
    .min(0, 'Value cannot be negative')
    .nullish()
    .or(z.literal(0)),
  lbs_per_case: z
    .number()
    .min(0, 'Serving size cannot be negative')
    .nullish()
    .or(z.literal(0)),
  current_aided_awareness: z
    .number()
    .min(0, 'Awareness cannot be negative')
    .max(100, 'Awareness cannot exceed 100%')
    .nullish()
    .or(z.literal(0)),
  current_national_trial_perc: z
    .number()
    .min(0, 'Trial percentage cannot be negative')
    .max(100, 'Trial percentage cannot exceed 100%')
    .nullish()
    .or(z.literal(0)),
  variant_count: z.number().optional(),
  variant_names: z.array(z.string()).optional(),
  variants: z
    .array(
      z
        .object({
          id: z.number().optional().nullable(),
          name: z.string().min(1, 'Name is required'),
          description: z.string().optional().nullable(),
          gtin: z.string().nullable(),
          sku: z.string().nullable(),
          units_per_case: z
            .union([z.number(), z.literal('')])
            .optional()
            .nullable(),
          case_weight: z
            .union([z.number(), z.literal('')])
            .optional()
            .nullable(),
          unit_weight: z
            .union([z.number(), z.literal('')])
            .optional()
            .nullable(),
          format_pack_size: z.string().nullable(),
          distributor_codes: z
            .array(
              z.object({
                id: z.number().optional(),
                distributor_name: z
                  .string()
                  .min(1, 'Distributor name is required'),
                distributor_code: z
                  .string()
                  .min(1, 'Distributor code is required'),
              })
            )
            .optional(),
        })
        .partial()
    )
    .optional()
    .nullable(),
})

const espValidationSchema = z.object({
  default_sales: z
    .number({ invalid_type_error: 'Default sales value is required' })
    .min(0, 'Default sales cannot be negative'),
  cuisine_sales: z.record(
    z.string(),
    z.number().min(0, 'Sales estimates cannot be negative')
  ),
  is_not_recurring: z.boolean().optional(),
})

const defaultValues: ProductFormValues = {
  name: 'New Product', // Valid default - user will replace
  format: '',
  product_format: '',
  price_per_case: 0,
  mfg_avg_list_price_per_lb: 1, // Valid default value
  avg_serving_size_per_lb: 1, // Valid default value
  est_monthly_churn_rate: 4,
  value_per_consumer_new_trial: 0,
  lbs_per_case: 0,
  current_aided_awareness: 0,
  current_national_trial_perc: 0,
  value_per_consumer_new_informed: 0,
  estimated_sales_potential: {},
  unit: 'lbs',
  variant_count: 0,
  variant_names: [],
  variants: [],
}

const defaultCuisineSales: Record<string, number> = {}
Cuisine50Options.forEach((cuisine) => {
  defaultCuisineSales[cuisine.value] = 0
})

const defaultESPValues: Product['estimated_sales_potential'] = {
  default_sales: 0,
  cuisine_sales: defaultCuisineSales,
  is_not_recurring: false,
}

const PAGE_SIZE = 100
const IDENTIFIER = 'PRODUCTS_TABLE'
const VARIANTS_IDENTIFIER = 'VARIANTS_TABLE'

type SortableFields = (keyof Product | string)[]

// Sortable fields for product catalog (includes variant_count)
const sortableFields: SortableFields = [
  'name',
  'format',
  'avg_serving_size_per_lb',
  'mfg_avg_list_price_per_lb',
  'est_monthly_churn_rate',
  'estimated_sales_potential.default_sales',
  'created',
  'modified',
  'variant_count',
]

interface ProductTableProps {
  tableKey: string
  onProductsCountChange?: (count: number) => void
  onVariantsCountChange?: (count: number) => void
}

export interface ProductTableHandle {
  openCreateProductModal: () => void
  openCreateVariantModal: () => void
}

const ProductTableComponent = forwardRef<ProductTableHandle, ProductTableProps>(
  function ProductTableComponent(
    { tableKey, onProductsCountChange, onVariantsCountChange },
    ref
  ) {
    const [step, setStep] = useState(0)
    const { isLimited } = useAuth()
    const createProductMutation = useCreateLegacyProduct()
    const updateProductMutation = useUpdateLegacyProduct()
    const deleteProductMutation = useDeleteLegacyProduct()
    const { enableSyndigoIntegration, isLoading: featureFlagsLoading } =
      useFeatureFlagService()

    const { data: syndigoSyncStatus } = useGetSyndigoSyncStatus()

    // View mode toggle
    const { viewMode } = useProductsViewModeStore()

    const productsFilterStore = filterStoreRepo.getStore(
      IDENTIFIER as FilterIdentifier
    )
    const { page, setPage } = productsFilterStore()
    const sortingStore = dataTableSortingStoreRepo.getStore(tableKey)
    const { sorting } = sortingStore()
    const [showModal, setShowModal] = useState<boolean>(false)
    const [showVariantModal, setShowVariantModal] = useState<boolean>(false)
    const [selectedProductID, setSelectedProductID] = useState<number>()
    const linkVariant = useTableLinkVariant()

    useImperativeHandle(
      ref,
      () => ({
        openCreateProductModal: () => setShowModal(true),
        openCreateVariantModal: () => setShowVariantModal(true),
      }),
      []
    )

    const filtersAsParams = useFilterParams(productsFilterStore)
    const { preferences, isLoading: isPreferencesLoading } = usePreferences()

    // Table context for row selection
    const tableContext = useDataTableContext()
    const {
      state: { rowSelection, totalSelectedRows, isAllRowsSelected },
      methods: { clearSelectedRows, setTotalRowsInBackend },
    } = tableContext

    const sortParams = useMemo(() => {
      const params: Record<string, unknown> = {}

      if (sorting?.length) {
        const sortField = sorting[0]?.id
        // Validate sort field is supported by current endpoint
        if (sortableFields.includes(sortField)) {
          params['sort'] = sorting[0].desc ? '-' + sortField : sortField
        }
      }

      return params
    }, [sorting, sortableFields])

    const filterAndSortParams = useMemo(
      () => ({
        ...filtersAsParams,
        ...sortParams,
      }),
      [filtersAsParams, sortParams]
    )

    const TABLE_QUERY_KEY = getTableQueryKey({
      tableKey: tableKey,
      filterParams: filterAndSortParams,
      page: page,
    })

    // DATA FETCHING
    const { isLoading: isProductsLoading, data } = useListProductsCatalog(
      {
        ...filterAndSortParams,
        page: page,
        page_size: PAGE_SIZE,
      },
      {
        query: {
          enabled: !featureFlagsLoading,
          refetchOnMount: 'always',
          // Custom query key needed so this table's row selection / pagination
          // doesn't share cache with other `useListProductsCatalog` consumers.

          queryKey: TABLE_QUERY_KEY as any,
        },
      }
    )
    // END DATA FETCHING

    // Fetch variants data when in variants mode (just for count)
    const variantsFilterStore = filterStoreRepo.getStore(
      VARIANTS_IDENTIFIER as FilterIdentifier
    )
    const variantsFiltersAsParams = useFilterParams(variantsFilterStore)

    const { data: variantsData } = useListVariantsCatalog(
      {
        ...variantsFiltersAsParams,
        page: 1,
        page_size: 1, // Just need the count, not the data
      },
      // Always enabled so the Variants tab label can show a count even while
      // the user is on the Products tab.
      { query: { enabled: true } }
    )

    // Row selection helpers
    const products = data?.results ?? []

    const selectedProductIds = useMemo(() => {
      return (
        products?.filter((_, i: number) => rowSelection[i])?.map((p) => p.id) ??
        []
      )
    }, [products, rowSelection])

    const unselectedProductIds = useMemo(() => {
      if (!isAllRowsSelected) return []
      return (
        products
          ?.filter((_, i: number) => !rowSelection[i])
          ?.map((p) => p.id) ?? []
      )
    }, [products, rowSelection, isAllRowsSelected])

    // Update total rows for "select all" functionality
    useEffect(() => {
      if (data?.count) {
        setTotalRowsInBackend(data.count)
      }
    }, [data?.count, setTotalRowsInBackend])

    // Propagate counts to the parent so the page-level tabs can show them.
    useEffect(() => {
      if (data?.count != null) onProductsCountChange?.(data.count)
    }, [data?.count, onProductsCountChange])
    useEffect(() => {
      if (variantsData?.count != null)
        onVariantsCountChange?.(variantsData.count)
    }, [variantsData?.count, onVariantsCountChange])

    const handleExport = async () => {
      if (viewMode === 'products') {
        await handleExportProducts()
      } else {
        await handleExportProductVariants()
      }
    }

    const handleExportProducts = async () => {
      try {
        // All available columns for export
        const columns = [
          'name',
          'product_format',
          'variant_count',
          'mfg_avg_list_price_per_lb',
          'avg_serving_size_per_lb',
          'est_monthly_churn_rate',
          'estimated_sales_potential',
          'created',
          'modified',
        ]

        // Determine include_ids and exclude_ids based on selection state
        const includeIds = isAllRowsSelected ? [] : selectedProductIds
        const excludeIds = isAllRowsSelected ? unselectedProductIds : []

        const params: Record<string, any> = {
          ...filterAndSortParams,
        }

        const blob = await exportProductsCatalogAsBlob(
          {
            selected_columns: columns,
            export_type: 'csv',
            ...(includeIds.length > 0 && { include_ids: includeIds }),
            ...(excludeIds.length > 0 && { exclude_ids: excludeIds }),
          },
          params
        )

        downloadFile(blob, 'products_export.csv')
        clearSelectedRows()
      } catch (error) {
        console.error('Export failed:', error)
        toast.error('Failed to export products. Please try again.')
      }
    }

    const handleSingleProductExport = async (productId: number) => {
      try {
        const columns = [
          'name',
          'product_format',
          'variant_count',
          'mfg_avg_list_price_per_lb',
          'avg_serving_size_per_lb',
          'est_monthly_churn_rate',
          'estimated_sales_potential',
          'created',
          'modified',
        ]
        const blob = await exportProductsCatalogAsBlob(
          {
            selected_columns: columns,
            export_type: 'csv',
            include_ids: [productId],
          },
          {}
        )
        downloadFile(blob, 'product_export.csv')
      } catch (error) {
        console.error('Export failed:', error)
        toast.error('Failed to export product. Please try again.')
      }
    }

    const handleExportProductVariants = async () => {
      try {
        // All available columns for variants export
        const columns = [
          'name',
          'sku',
          'gtin',
          'description',
          'product_name',
          'match_status',
          'format_pack_size',
          'units_per_case',
          'case_weight',
          'case_weight_unit',
          'unit_weight',
          'unit_weight_unit',
          'avg_price',
          'created',
          'modified',
        ]

        const variantsSortingStore = dataTableSortingStoreRepo.getStore(
          `${tableKey}-variants`
        )
        const variantsSorting = variantsSortingStore.getState().sorting

        const variantsSortParams: Record<string, unknown> = {}
        if (variantsSorting?.length) {
          const sortField = variantsSorting[0]?.id
          variantsSortParams['sort'] = variantsSorting[0].desc
            ? '-' + sortField
            : sortField
        }

        const params: Record<string, any> = {
          ...variantsFiltersAsParams,
          ...variantsSortParams,
        }

        const blob = await exportVariantsCatalogAsBlob(
          {
            selected_columns: columns,
            export_type: 'csv',
          },
          params
        )

        downloadFile(blob, 'variants_export.csv')
        clearSelectedRows()
      } catch (error) {
        console.error('Export failed:', error)
        toast.error('Failed to export variants. Please try again.')
      }
    }

    const bulkActionItems = [
      {
        label: 'Export',
        icon: <PiExport size={20} />,
        callback: handleExport,
      },
    ]

    const reportingUnit = preferences?.reporting_unit
    const isLoading =
      isProductsLoading || isPreferencesLoading || featureFlagsLoading

    const espForm = useForm({
      defaultValues: defaultESPValues,
      resolver: zodResolver(espValidationSchema),
      mode: 'onChange',
    })

    const productForm = useForm<ProductFormValues>({
      resolver: zodResolver(productShape),
      mode: 'all', // Validate on mount, change, blur, and submit
      defaultValues: {
        ...defaultValues,
        unit: reportingUnit,
      },
      shouldUseNativeValidation: false,
      criteriaMode: 'all',
    })

    const onErrorGoToRightStep = (error: Record<string, any>) => {
      const firstStepFields = [
        'name',
        'avg_serving_size_per_lb',
        'mfg_avg_list_price_per_lb',
        'est_monthly_churn_rate',
        'value_per_consumer_new_trial',
        'lbs_per_case',
        'current_aided_awareness',
        'current_national_trial_perc',
      ]
      const secondStepFields = ['default_sales', 'cuisine_sales']
      const errorKeys = Object.keys(error)
      if (errorKeys.some((key) => firstStepFields.includes(key))) {
        setStep(0)
      } else if (errorKeys.some((key) => secondStepFields.includes(key))) {
        setStep(1)
      }
    }

    const onSubmitCreate = productForm.handleSubmit(async (values) => {
      let esp = {} as Product['estimated_sales_potential']
      await espForm.handleSubmit((data) => (esp = data), console.warn)()

      // Extract is_not_recurring from esp to top-level (new schema)
      const { is_not_recurring, ...espWithoutFlag } = esp

      const { unit: _unit, ...valuesWithoutUnit } = values

      // unit field is no longer saved and validated product-by-product and is a gloabl user preference
      const payload = {
        ...valuesWithoutUnit,
        format: values.product_format,
        estimated_sales_potential: espWithoutFlag,
        is_not_recurring: is_not_recurring || false,
        variants: values.variants || [],
      }

      const [err] = await to(
        createProductMutation.mutateAsync({
          data: payload as unknown as CreateLegacyProductMutationRequest,
        })
      )
      if (err) {
        handleFormError(err, productForm.setError)
        onErrorGoToRightStep(err)
        return
      }

      // Cache invalidation is handled by the wrapped mutation hook's
      // `onSuccess` (see `createLegacyProduct` entry in the products
      // invalidation registry). No manual `refetch` / `invalidateQueries`
      // needed here.
      toast.success('Product created successfully')

      hideModal()
    })

    const onSubmitUpdate = async () => {
      const values = productForm.getValues()
      let esp = {} as Product['estimated_sales_potential']
      await espForm.handleSubmit((data) => (esp = data), console.warn)()

      if (!selectedProductID) return

      // Ensure cuisine_sales values default to 0 if falsy
      if (esp.cuisine_sales) {
        Object.keys(esp.cuisine_sales).forEach((cuisine) => {
          if (!esp.cuisine_sales![cuisine]) {
            esp.cuisine_sales![cuisine] = 0
          }
        })
      }

      // Extract is_not_recurring from esp to top-level (new schema)
      const { is_not_recurring, ...espWithoutFlag } = esp

      // Prepare update payload
      const updatePayload: any = {
        ...values,
        format: values.product_format,
        estimated_sales_potential: espWithoutFlag,
        is_not_recurring: is_not_recurring || false,
        // Always include variants array (even if empty) to be explicit
        variants: values.variants || [],
        // Always skip variant deletion (new catalog behavior)
        skip_variant_deletion: true,
      }

      const [err] = await to(
        updateProductMutation.mutateAsync({
          id: selectedProductID,
          data: updatePayload as UpdateLegacyProductMutationRequest,
        })
      )
      if (err) {
        handleFormError(err, productForm.setError)
        onErrorGoToRightStep(err)
        return
      }

      // Cache invalidation is handled by the wrapped mutation hook's
      // `onSuccess` (see `updateLegacyProduct` entry in the products
      // invalidation registry). No manual `refetch` / `invalidateQueries`
      // needed here.
      toast.success('Product updated successfully')

      hideModal()
    }

    const columns = useMemo<ColumnDef<ProductCatalog, any>[]>(() => {
      const allColumns: ColumnDef<ProductCatalog, any>[] = [
        {
          accessorKey: 'name',
          header: () => '',
          enableHiding: false,
          meta: {
            headerTitle: 'Product Name',
            isFirstColumn: true,
            isEditable: false,
          },
          size: 300,
        },
        {
          accessorKey: 'mfg_avg_list_price_per_lb',
          header: () => {
            const tooltip = preferences?.tooltips?.products_avg_revenue_per_unit
            const name = reportingUnit ? getSingularUnit(reportingUnit) : 'Unit'
            return (
              <div className="flex items-center gap-1">
                <span>
                  {isPreferencesLoading
                    ? 'Avg. Price per --'
                    : `Avg. Price per ${name}`}
                </span>
                {tooltip && (
                  <HelpTooltip
                    title={tooltip.title}
                    content={tooltip.content}
                  />
                )}
              </div>
            )
          },
          meta: {
            headerTitle: reportingUnit
              ? `Avg. Price per ${getSingularUnit(reportingUnit)}`
              : 'Avg. Price per Unit',
          },
          size: 150,
          cell: (info) => {
            const value = info.getValue()
            if (!value || isPreferencesLoading || !reportingUnit) return '-'
            return `$${formatTwoDecimal(value)}`
          },
        },
        {
          accessorKey: 'est_monthly_churn_rate',
          header: () => {
            const tooltip =
              preferences?.tooltips?.products_est_monthly_churn_rate
            return (
              <div className="flex items-center gap-1">
                <span>Est. Monthly Churn Rate</span>
                {tooltip && (
                  <HelpTooltip
                    title={tooltip.title}
                    content={tooltip.content}
                  />
                )}
              </div>
            )
          },
          meta: {
            headerTitle: 'Est. Monthly Churn Rate',
          },
          size: 150,
          cell: (info) => {
            const value = info.getValue()
            return value ? `${formatPercentage(info.getValue())}%` : '-'
          },
        },
        {
          accessorKey: 'estimated_sales_potential.default_sales',
          header: () => {
            const tooltip = preferences?.tooltips?.products_avg_sales_per_month
            return (
              <div className="flex items-center gap-1">
                <span>Avg. Sales per Month</span>
                {tooltip && (
                  <HelpTooltip
                    title={tooltip.title}
                    content={tooltip.content}
                  />
                )}
              </div>
            )
          },
          meta: {
            headerTitle: 'Avg. Sales per Month',
          },
          size: 150,
          cell: (info) => {
            const value = info.getValue()
            if (!value || isPreferencesLoading) return '-'
            return `${formatInteger(value)} ${reportingUnit}`
          },
        },
        {
          id: 'variant_count',
          accessorKey: 'variant_count',
          header: 'Variants',
          size: 250,
          enableHiding: true,
          cell: (info) => {
            const row = info.row.original
            const variantNames = row.variant_names || []
            const totalCount = row.variant_count || 0

            // If no variants, show dash
            if (totalCount === 0 || variantNames.length === 0) {
              return <div className="text-gray-400">-</div>
            }

            if (linkVariant === 'plain') {
              // DS2026: one truncated pill + clickable "+N" chip popover for
              // the rest, matching the SamplesTable products column.
              const [first, ...rest] = variantNames
              const overflowCount = totalCount - 1
              return (
                <div className="flex items-center gap-1">
                  <span
                    className="inline-flex items-center max-w-[180px] px-2.5 py-0.5 rounded-2xl text-sm font-medium leading-5 tracking-[-0.2px] bg-gray-100 text-gray-700 truncate"
                    title={first}
                  >
                    {first}
                  </span>
                  {overflowCount > 0 && (
                    <PillOverflowChip count={overflowCount}>
                      {rest.map((name, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-2xl text-sm font-medium leading-5 tracking-[-0.2px] bg-gray-100 text-gray-700"
                        >
                          {name}
                        </span>
                      ))}
                    </PillOverflowChip>
                  )}
                </div>
              )
            }

            // Show pills for first few, then "+X more"
            return (
              <PillListCell
                items={variantNames}
                maxColumnWidth={230}
                maxPillWidth={120}
                totalCount={totalCount}
              />
            )
          },
        },
        ...(!isLimited
          ? [
              {
                id: 'edit',
                header: () => <div className="w-full text-center">Actions</div>,
                size: 70,
                enableHiding: false,
                meta: {
                  isLastColumn: true,
                  isEditable: false,
                  headerDisableOrdering: true,
                },
                cell: ({ row }: any) => {
                  const productId = row.original?.id
                  const actions = [
                    createEditAction(() => handleRowEdit(row.index)),
                    {
                      label: 'Export',
                      icon: PiExport,
                      onClick: () =>
                        productId && handleSingleProductExport(productId),
                    },
                    createDeleteAction(() => handleRowDelete(row.index)),
                  ]
                  return (
                    <ActionsDropdown
                      actions={actions}
                      className="justify-center"
                    />
                  )
                },
              },
            ]
          : []),
      ]

      // Always include all columns (new catalog behavior)
      return allColumns
    }, [data?.results, reportingUnit, isLimited])

    function hideModal() {
      setShowModal(false)
      productForm.reset({
        ...defaultValues,
        unit: reportingUnit,
      })
      espForm.reset(defaultESPValues)
      setSelectedProductID(undefined)
    }

    async function handleRowDelete(index: number) {
      const id = data?.results?.[index]?.id
      if (!id) return

      if (
        await dialogConfirm(
          'Are you sure you want to delete this product? This action will also delete any deals associated with the product.',
          'Delete Product'
        )
      ) {
        try {
          await deleteProductMutation.mutateAsync({ id })
          toast.success('Product deleted successfully')
        } catch {
          /* empty */
        }
        // Wrapped mutation handles cache invalidation on success.
      }
    }

    function handleRowEdit(index: number) {
      const selectedProduct = data?.results?.find(
        (product) => product.id === data?.results?.[index]?.id
      )

      if (!selectedProduct) return

      setShowModal(true)
      setSelectedProductID(data?.results?.[index]?.id)

      // Structure ESP form values
      // Prefer top-level is_not_recurring (new schema), fallback to JSON for backwards compat
      const esp = selectedProduct.estimated_sales_potential as
        | Product['estimated_sales_potential']
        | undefined
      const espInitialValues = {
        default_sales: esp?.default_sales,
        cuisine_sales: esp?.cuisine_sales,
        is_not_recurring:
          selectedProduct.is_not_recurring ?? esp?.is_not_recurring ?? false,
      }

      espForm.reset(espInitialValues)
      if (data) {
        // For new catalog mode, ensure variants array is empty (for NEW variants only)
        // Existing variants are displayed via variant_names and variant_count
        productForm.reset({
          ...(selectedProduct as unknown as ProductFormValues),
          variants: [], // Always start with empty array for new variants
        })
      }
    }

    return (
      <DataTableContainer>
        {viewMode === 'products' ? (
          <DataTable
            key={tableKey}
            tableHeader={
              <S.SearchContainer>
                <S.SearchContainerFilters>
                  <S.TableSearchContainer>
                    <TableSearch
                      filterStore={productsFilterStore}
                      searchPlaceholder="Search by product name, variant name, SKU, or UPC/GTIN..."
                      tableKey={tableKey}
                    />
                  </S.TableSearchContainer>
                  <S.TableButtonsContainer>
                    <ProductsFilterset
                      filterIdentifier={IDENTIFIER as FilterIdentifier}
                    />
                    <ColumnSelectorRecipient tableKey={tableKey} />
                  </S.TableButtonsContainer>
                </S.SearchContainerFilters>
                <div className="flex flex-row justify-between gap-4">
                  {linkVariant !== 'plain' && (
                    <TableButtonAction
                      selectedRowsCount={totalSelectedRows}
                      entityType="products"
                      items={bulkActionItems}
                    />
                  )}
                  <FilterChips
                    identifier={IDENTIFIER}
                    store={productsFilterStore}
                    clearAllButton
                    showActive
                  />
                </div>
              </S.SearchContainer>
            }
            selectHeaderActions={
              linkVariant === 'plain' ? (
                <TableButtonAction
                  selectedRowsCount={totalSelectedRows}
                  entityType="products"
                  items={bulkActionItems}
                />
              ) : undefined
            }
            tableKey={tableKey}
            loading={isLoading}
            data={data?.results ?? []}
            columns={columns}
            sortableFields={sortableFields}
            enableRowSelection={true}
            virtualizeRows={false}
            isPaginationEnabled={true}
            stickyLastColumn
            paginationOptions={{
              totalRows: data?.count ?? 0,
              pageSize: PAGE_SIZE,
              setPage: setPage,
              page: page,
              isPaginationLoading: isLoading,
            }}
            selectAllText="Select Products"
            footerControls={
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-export">
                    {totalSelectedRows === 0
                      ? 'Export all products'
                      : `Export ${totalSelectedRows} selected product${totalSelectedRows !== 1 ? 's' : ''}`}
                  </Tooltip>
                }
              >
                <div>
                  <IconMiniButton
                    onClick={handleExportProducts}
                    variant={linkVariant === 'plain' ? 'secondary' : undefined}
                    className={
                      linkVariant === 'plain'
                        ? '!text-neutral-800 !border-neutral-300 hover:!bg-neutral-100 hover:!text-neutral-900 hover:!border-neutral-300 hover:!shadow-none'
                        : undefined
                    }
                  >
                    <PiDownloadSimple size={18} />
                  </IconMiniButton>
                </div>
              </OverlayTrigger>
            }
          />
        ) : (
          <VariantSearchTable
            mode="standalone"
            tableKey={`${tableKey}-variants`}
            showFilters={true}
          />
        )}

        {enableSyndigoIntegration && syndigoSyncStatus && (
          <div className="flex gap-4 items-center text-sm mt-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-700">
                Syndigo Status:
              </span>
              <span
                className="px-3 py-1 rounded-full text-white font-medium capitalize"
                style={{
                  backgroundColor: getStatusColor(
                    syndigoSyncStatus.status as Parameters<
                      typeof getStatusColor
                    >[0]
                  ),
                }}
              >
                {syndigoSyncStatus.status.replace('_', ' ')}
              </span>
            </div>
            <div className="h-6 w-px bg-gray-300" />
            <div className="text-gray-500">
              <strong>Last Sync:</strong>{' '}
              {syndigoSyncStatus.lastSyncAt
                ? formatDateWithTimeAndMinutes(syndigoSyncStatus.lastSyncAt)
                : 'Never'}
            </div>
            <div className="text-gray-500">
              <strong>Last Full Sync:</strong>{' '}
              {syndigoSyncStatus.lastFullSyncAt
                ? formatDateWithTimeAndMinutes(syndigoSyncStatus.lastFullSyncAt)
                : 'Never'}
            </div>
          </div>
        )}

        <ProductModal
          show={showModal}
          handleClose={hideModal}
          selectedProductID={selectedProductID}
          defaultValues={defaultValues}
          productForm={productForm}
          espForm={espForm}
          onSubmit={async () =>
            selectedProductID ? onSubmitUpdate() : onSubmitCreate()
          }
          step={step}
          setStep={setStep}
        />

        <StandaloneVariantModal
          show={showVariantModal}
          onHide={() => setShowVariantModal(false)}
        />
      </DataTableContainer>
    )
  }
)

ProductTableComponent.displayName = 'ProductTable'

export const ProductTable = forwardRef<
  ProductTableHandle,
  ProductTableProps & { defaultColumnVisibility?: IColumnVisibility<any> }
>(function ProductTable({ tableKey, defaultColumnVisibility, ...rest }, ref) {
  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={defaultColumnVisibility}
      preferredGroupOrder={['Product Details', 'Product Metrics', 'Activity']}
    >
      <DataTableProvider tableKey={tableKey}>
        <ProductTableComponent ref={ref} tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
})
